<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { type DataTableColumns, type SelectOption } from 'naive-ui';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import {
  addPromptColumn,
  createFileReviews,
  createOverwrite,
  getReviewCost,
  getReviewTableByUid,
  updateOverwrite,
  type FileReviewCreateUser,
  type PromptCreate,
  type PromptInDb,
  type PromptUpdate,
  type ReviewCostResponse,
  type ReviewTableSearch,
  type ReviewTableSearchQuery,
} from '@/api';
import AgentEditDrawer from '@/components/AgentEditDrawer.vue';
import CostPreviewNotice from '@/components/CostPreviewNotice.vue';
import FileSelector from '@/components/FileSelector.vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaRemovableList from '@/components/design/composite/GaiaRemovableList.vue';
import GaiaPanel from '@/components/design/composite/GaiaPanel.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import GaiaExtractionDetail from '@/components/design/review/GaiaExtractionDetail.vue';
import GaiaSummaryCard from '@/components/design/review/GaiaSummaryCard.vue';
import { gaiaContentWidth, gaiaFonts } from '@/components/design/themes/tokens';
import type { GaiaExtractionEditValues } from '@/components/design/domain/extraction/GaiaExtractionFieldRow.vue';
import { useReviewLive } from '@/composables/review/useReviewLive';
import { useReviewPolling } from '@/composables/useReviewPolling';
import { buildExtractedFieldFromCell } from '@/services/review/extractedField';
import {
  buildDisplayToActualValueMaps,
  buildInsightColumns,
  generateColumnsCacheKey,
  transformToTableRowData,
  NO_DATA_FILTER,
  type ExtractionCellData,
  type InsightDisplayMode,
  type TableRowData,
} from '@/services/review/insightGrid';
import { downloadReviewCsv } from '@/services/review/reviewCsv';
import { useAuthStore } from '@/stores/auth';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { useReviewStore } from '@/stores/reviewStore';
import { useVaultStore, type VaultItem } from '@/stores/vaultStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { buildOverwritePayload } from '@/utils/overwrite';
import { formatFilters } from '@/utils/tableFilters';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * A multi-file review as a spreadsheet: one row per file, one column per
 * extraction prompt, one cell per answer.
 *
 * Columns and rows are built by `services/review/insightGrid` — the ~700 lines
 * of `h()` that used to live here — and a cell opens the same
 * `GaiaExtractionDetail` body the single-file detail view uses, in a drawer
 * rather than a modal.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();
const gaiaStore = useGaiaStore();
const integrationsStore = useIntegrationsStore();
const kanbanStore = useKanbanStore();
const organizationStore = useOrganizationStore();
const promptCollectionsStore = usePromptCollectionsStore();
const reviewStore = useReviewStore();
const vaultStore = useVaultStore();

const reviewId = computed(() => route.params.id as string);
const currentReview = computed(() => reviewStore.current);

const loading = ref(true);
const tableLoading = ref(false);
const error = ref<string | null>(null);

const tableSearchData = ref<ReviewTableSearch | null>(null);
const totalRows = ref(0);
const totalReviewRows = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

const displayMode = ref<InsightDisplayMode>('value');
const activeFilters = ref<Record<string, unknown>>({});
const backendFilters = ref<ReturnType<typeof formatFilters>>({});
const currentSortColumn = ref<string | null>(null);
const currentSortDirection = ref<'asc' | 'desc'>('asc');

/** Rows sampled across the whole review, so the filter menus offer every value. */
const allTableDataForFilters = ref<TableRowData[]>([]);
const filterDataLoading = ref(false);
const displayToActualValueMaps = ref<Record<string, Map<string, unknown> | undefined>>({});

const cachedColumns = ref<DataTableColumns<TableRowData>>([]);
const columnsCacheKey = ref<string>('');

const selectedRowKey = ref<string | null>(null);
const selectedColumnKey = ref<string | null>(null);
const cellDrawerOpen = ref(false);

const showCsvExportModal = ref(false);
const csvExportLoading = ref(false);
const csvExportOptions = ref({
  include_prompt: false,
  include_evaluation: true,
  include_reasons: true,
});

const showAgentDrawer = ref(false);
const isNewPrompt = ref(false);
const currentAgentPrompt = ref<PromptInDb | null>(null);
const addPromptAgentCount = ref(1);

const showAddFileModal = ref(false);
const processingAddFile = ref(false);
const addFileSelectedKeys = ref<string[]>([]);
const addFileSelectorRef = ref<{
  getSelectedFiles: () => unknown[];
  uploadSelectedFiles: () => Promise<string[]>;
} | null>(null);
const uploadSelectedCount = ref(0);
const addFileCost = ref<ReviewCostResponse | null>(null);
const addFileCostLoading = ref(false);
const addFileCostError = ref<string | null>(null);
const addFileCostSignature = ref('');
const addFileCostRequestSequence = ref(0);

const canEdit = computed(() => authStore.userPermissions.can_update_reviews);
const isAdmin = computed(() => authStore.userPermissions.is_org_admin);

const hasSummary = computed(() => Boolean(currentReview.value?.markdown_summary));
const markdownSummary = computed(() => currentReview.value?.markdown_summary ?? '');

/**
 * The review's prompt columns, for the Columns popover. Deleting one belongs
 * here rather than in the column header: naive fills that cell with its own
 * sort arrow and filter funnel, which leave nothing clickable beside them.
 * Same mechanic the board uses for its kanban columns.
 */
const columnMenuItems = computed(() =>
  (tableSearchData.value?.static_prompt_copies ?? [])
    .map(prompt => ({ key: prompt.uid, label: prompt.name }))
    // Alphabetical, matching the order `columnKeysOf` puts the columns in, so a
    // row in this list and its column in the grid are found the same way.
    .sort((a, b) => a.label.localeCompare(b.label))
);

const monoFont = gaiaFonts.mono;

/** Kept in step with `buildInsightColumns`, which sizes the columns it builds. */
const FILENAME_COLUMN_WIDTH = 300;
const PROMPT_COLUMN_WIDTH = 200;

const DISPLAY_MODE_OPTIONS: SelectOption[] = [
  { label: 'Value', value: 'value' },
  { label: 'Reason', value: 'reason' },
  { label: 'Page', value: 'page' },
  { label: 'Highlighted text', value: 'highlighted_text' },
  { label: 'Evaluation', value: 'evaluation' },
];

// ── Rows and columns ────────────────────────────────────────────────────────

const tableData = computed<TableRowData[]>(() =>
  tableSearchData.value?.rows ? transformToTableRowData(tableSearchData.value.rows) : []
);

const hasActiveFilters = computed(() => Object.keys(activeFilters.value).length > 0);

function cellOf(row: TableRowData, key: string): ExtractionCellData | undefined {
  const value = row[key];
  return value && typeof value === 'object' && 'uid' in value
    ? (value as ExtractionCellData)
    : undefined;
}

const selectedCell = computed<ExtractionCellData | null>(() => {
  if (!selectedRowKey.value || !selectedColumnKey.value) return null;
  const row = tableData.value.find(item => item.file_review_uid === selectedRowKey.value);
  return row ? (cellOf(row, selectedColumnKey.value) ?? null) : null;
});

/** The drawer reads the same view-model the single-file detail view builds. */
const selectedField = computed(() => {
  const cell = selectedCell.value;
  if (!cell) return null;
  const prompt = tableSearchData.value?.static_prompt_copies.find(
    item => item.name === cell.prompt_name
  );
  return buildExtractedFieldFromCell(cell, {
    fieldType: prompt?.response_type ?? 'str',
    enumOptions: typeof prompt?.response_type === 'object' ? prompt.response_type : undefined,
  });
});

/**
 * The width the grid needs, not a guess: naive lays the table out at `scroll-x`
 * and squeezes fixed-width columns to fit when it is too small. One filename
 * column plus one 200px column per prompt, matching `buildInsightColumns`.
 */
const tableScrollX = computed(() => {
  const promptColumns = Math.max(0, cachedColumns.value.length - 1);
  return FILENAME_COLUMN_WIDTH + promptColumns * PROMPT_COLUMN_WIDTH;
});

function rebuildColumns(): void {
  cachedColumns.value = buildInsightColumns({
    rows: tableData.value,
    filterRows: allTableDataForFilters.value,
    mode: displayMode.value,
    // Org-configured answer colours are not modelled per column by the API, so
    // every column shares the product's default palette.
    answerColor: (_key, answer) => DEFAULT_ANSWER_COLORS[answer],
    onOpenCell: (row, key) => {
      selectedRowKey.value = row.file_review_uid;
      selectedColumnKey.value = key;
      cellDrawerOpen.value = true;
    },
    onOpenContract: row => {
      void router.push({
        name: 'review-contract',
        params: { reviewId: reviewId.value },
        // `file_review_uid`, not the file's uid: two reviews can share a file,
        // and each needs its own destination.
        query: { fileReviewUid: row.file_review_uid, source: 'detail' },
      });
    },
  });
  displayToActualValueMaps.value = buildDisplayToActualValueMaps(
    allTableDataForFilters.value.length > 0 ? allTableDataForFilters.value : tableData.value,
    displayMode.value
  );
}

/** Cell washes for the four canonical evaluation answers. */
const DEFAULT_ANSWER_COLORS: Record<string, string | undefined> = {
  High: '#f4c7c6',
  Medium: '#faad14',
  'Human Review': '#ffecb8',
  Low: '#cff2cd',
};

watch(
  [tableData, displayMode, allTableDataForFilters],
  () => {
    const nextKey = generateColumnsCacheKey(
      tableData.value,
      displayMode.value,
      allTableDataForFilters.value.length
    );
    // ~40 columns of render functions; rebuilding them on every poll tick is
    // what the cache key avoids.
    if (nextKey !== columnsCacheKey.value) {
      columnsCacheKey.value = nextKey;
      rebuildColumns();
    }
  },
  { immediate: true, deep: true }
);

// ── Fetching ────────────────────────────────────────────────────────────────

async function fetchTableData(reviewUid: string, showLoading = true): Promise<void> {
  if (showLoading) tableLoading.value = true;
  error.value = null;

  try {
    const query: ReviewTableSearchQuery = {
      row_limit: pageSize.value,
      column_limit: 50,
      column_filters: backendFilters.value,
      row_sort_direction: currentSortDirection.value,
      column_sort_direction: currentSortDirection.value,
    };
    if (currentSortColumn.value) query.sorting_column = currentSortColumn.value;
    if (currentPage.value > 1) query.row_offset = (currentPage.value - 1) * pageSize.value;

    const response = await getReviewTableByUid({ path: { review_uid: reviewUid }, body: query });
    if (!response.data) throw new Error('Failed to get review table');

    tableSearchData.value = response.data.review;
    totalRows.value = response.data.row_count;
  } catch (error_) {
    error.value = `Error fetching table data: ${error_ instanceof Error ? error_.message : String(error_)}`;
    logger.error('Error fetching table data:', error_);
  } finally {
    if (showLoading) tableLoading.value = false;
  }
}

/**
 * Filter menus have to offer values from the whole review, not just the page on
 * screen. Sampled rather than exhaustive: up to four pages of 50, taken from
 * both ends, which covers the distinct values without pulling a 2000-row review
 * into the browser.
 */
async function fetchAllDataForFilters(reviewUid: string): Promise<void> {
  if (filterDataLoading.value) return;

  const PAGE = 50;
  const MAX_PAGES = 4;

  filterDataLoading.value = true;
  try {
    const baseQuery: ReviewTableSearchQuery = {
      row_limit: PAGE,
      column_limit: 50,
      column_filters: {},
      row_sort_direction: 'asc',
      column_sort_direction: 'asc',
    };

    const first = await getReviewTableByUid({
      path: { review_uid: reviewUid },
      body: baseQuery,
    });
    if (!first.data) throw new Error('Failed to fetch sample data for filters');

    totalReviewRows.value = first.data.row_count;

    if (first.data.row_count <= PAGE) {
      allTableDataForFilters.value = transformToTableRowData(first.data.review.rows ?? []);
      return;
    }

    const pageCount = Math.min(MAX_PAGES, Math.ceil(first.data.row_count / PAGE));
    const offsets = new Set<number>();
    for (let page = 0; page < Math.min(2, pageCount); page++) offsets.add(page * PAGE);
    for (let page = Math.max(0, pageCount - 2); page < pageCount; page++) offsets.add(page * PAGE);

    const responses = await Promise.all(
      [...offsets].map(row_offset =>
        getReviewTableByUid({
          path: { review_uid: reviewUid },
          body: { ...baseQuery, row_offset },
        })
      )
    );

    allTableDataForFilters.value = responses.flatMap(response =>
      response.data ? transformToTableRowData(response.data.review.rows ?? []) : []
    );
  } catch (error_) {
    logger.error('Error fetching sample data for filters:', error_);
    // Fall back to the page on screen — narrower menus, but not empty ones.
    allTableDataForFilters.value = [...tableData.value];
  } finally {
    filterDataLoading.value = false;
  }
}

async function fetchReviewData(): Promise<void> {
  if (!reviewId.value) return;

  loading.value = true;
  error.value = null;
  try {
    const review = await reviewStore.getReviewByUid(reviewId.value);
    if (!review) {
      error.value = `Review with ID "${reviewId.value}" was not found.`;
      return;
    }
    selectedState.value = review.kanban_state?.uid ?? null;
    await fetchAllDataForFilters(reviewId.value);
    await fetchTableData(reviewId.value);
  } catch (error_) {
    logger.error('Error fetching review data:', error_);
    error.value =
      error_ instanceof Error ? error_.message : 'An error occurred while loading the review';
  } finally {
    loading.value = false;
  }
}

/** One refetch for this route: the table endpoint returns rows *and* columns. */
async function refreshTableSilently(): Promise<void> {
  if (!reviewId.value) return;
  await fetchTableData(reviewId.value, false);
  await fetchAllDataForFilters(reviewId.value);
  rebuildColumns();
}

watch([currentPage, pageSize], () => {
  if (reviewId.value) void fetchTableData(reviewId.value);
});

// ── Sorting and filtering ───────────────────────────────────────────────────

function handleSortChange(
  sortState: { columnKey: string; order: 'ascend' | 'descend' | false } | null
): void {
  if (sortState?.order) {
    currentSortColumn.value = sortState.columnKey;
    currentSortDirection.value = sortState.order === 'ascend' ? 'asc' : 'desc';
  } else {
    currentSortColumn.value = null;
    currentSortDirection.value = 'asc';
  }
  currentPage.value = 1;
  if (reviewId.value) void fetchTableData(reviewId.value);
}

/**
 * The filter menus offer display strings; the backend filters on stored values,
 * so each pick is translated back through the per-column map.
 */
function handleFiltersChange(filterState: unknown): void {
  activeFilters.value = filterState ? (filterState as Record<string, unknown>) : {};

  const translated: Record<string, unknown[]> = {};
  for (const [key, value] of Object.entries(activeFilters.value)) {
    if (!Array.isArray(value)) continue;

    const actualValues = value.map(displayValue => {
      // Both mean "nothing was recorded", which the backend spells as null.
      if (displayValue === NO_DATA_FILTER || displayValue === 'N/A') return null;
      const mapped = displayToActualValueMaps.value[key]?.get(String(displayValue));
      if (mapped !== undefined) return mapped;
      // "Empty" survived a run that produced an empty string.
      if (displayValue === 'Empty') return '';
      return displayValue;
    });

    if (actualValues.length > 0) translated[key] = actualValues;
  }

  backendFilters.value = formatFilters(translated);
  currentPage.value = 1;
  if (reviewId.value) void fetchTableData(reviewId.value);
}

function resetFilters(): void {
  activeFilters.value = {};
  backendFilters.value = {};
  currentSortColumn.value = null;
  currentSortDirection.value = 'asc';
  currentPage.value = 1;
  if (reviewId.value) {
    void fetchTableData(reviewId.value);
    void fetchAllDataForFilters(reviewId.value);
  }
}

// ── Cell edits ──────────────────────────────────────────────────────────────

async function submitOverwrite(values: Map<string, GaiaExtractionEditValues>): Promise<void> {
  const cell = selectedCell.value;
  const field = selectedField.value;
  if (!cell?.file_review_uid || !field) return;
  if (!canEdit.value) {
    message.error('You do not have permission to edit reviews.');
    return;
  }

  const existing = field.overwrite_uid
    ? {
        value: field.overwritten_value,
        reason: field.overwritten_reason,
        page: field.overwritten_page,
        highlighted_text: field.overwritten_highlighted_text,
        evaluation: field.overwritten_evaluation,
        evaluation_reason: field.overwritten_evaluation_reason,
      }
    : null;

  try {
    const body = buildOverwritePayload(existing, values);
    await (field.overwrite_uid
      ? updateOverwrite({
          path: {
            review_uid: reviewId.value,
            file_review_uid: cell.file_review_uid,
            extraction_uid: cell.uid,
            extraction_overwrite_uid: field.overwrite_uid,
          },
          body,
        })
      : createOverwrite({
          path: {
            review_uid: reviewId.value,
            file_review_uid: cell.file_review_uid,
            extraction_uid: cell.uid,
          },
          body,
        }));

    message.success('Changes saved');
    await refreshTableSilently();
  } catch (error_) {
    logger.error('Error submitting changes:', error_);
    message.error('Failed to submit changes. Please try again.');
  }
}

async function handleApproval(approved: boolean): Promise<void> {
  const cell = selectedCell.value;
  if (!cell?.file_review_uid) return;
  try {
    const updated = await reviewStore.updateExtractionByUid(
      reviewId.value,
      cell.file_review_uid,
      cell.uid,
      approved
    );
    if (!updated) {
      message.error('Failed to update approval status');
      return;
    }
    message.success(`Extraction ${approved ? 'confirmed' : 'unconfirmed'} successfully`);
    await refreshTableSilently();
  } catch (error_) {
    logger.error('Error updating approval status:', error_);
    message.error('Failed to update approval status');
  }
}

// ── Kanban state ────────────────────────────────────────────────────────────

const selectedState = ref<string | null>(null);
const kanbanStatesFetched = ref(false);

const stateOptions = computed(() =>
  kanbanStore.visibleKanbanStates.map(state => ({ key: state.uid, label: state.name }))
);

const currentStateName = computed(
  () =>
    kanbanStore.kanbanStates.find(state => state.uid === selectedState.value)?.name ?? 'No state'
);

/** The states are only worth a request once someone opens the menu. */
async function loadStates(): Promise<void> {
  if (kanbanStatesFetched.value) return;
  try {
    await kanbanStore.fetchKanbanStates();
    kanbanStatesFetched.value = true;
  } catch (error_) {
    logger.error('Error fetching kanban states:', error_);
  }
}

async function handleStateChange(stateUid: string): Promise<void> {
  if (!currentReview.value) return;
  if (!canEdit.value) {
    message.error('You do not have permission to update reviews.');
    return;
  }
  try {
    // One write: update-sort-indexes sets the state alongside the position, so
    // the full-replace updateReview that used to follow it was redundant and a
    // lost-update risk on every other field.
    await kanbanStore.moveReview(currentReview.value.uid, stateUid, 0);
    selectedState.value = stateUid;
    message.success('State updated successfully');
  } catch (error_) {
    logger.error('Error updating review state:', error_);
    message.error('Failed to update state');
  }
}

// ── Prompt columns ──────────────────────────────────────────────────────────

const pendingColumnDelete = ref<string | null>(null);

async function confirmColumnDelete(): Promise<void> {
  const promptUid = pendingColumnDelete.value;
  const review = currentReview.value;
  if (!promptUid || !review) return;

  try {
    const success = await reviewStore.deletePromptColumn(review.uid, promptUid);
    if (success) {
      message.success('Prompt column deleted successfully');
      columnsCacheKey.value = '';
      await refreshTableSilently();
    }
  } catch (error_) {
    logger.error('Error deleting prompt column:', error_);
    message.error('Failed to delete prompt column');
  } finally {
    pendingColumnDelete.value = null;
  }
}

function handleCreatePrompt(): void {
  isNewPrompt.value = true;
  addPromptAgentCount.value = 1;
  currentAgentPrompt.value = {
    uid: '',
    created_at: new Date().toISOString(),
    name: '',
    prompt: '',
    response_type: 'str',
    example_data: null,
    evaluation_prompt: null,
    evaluation_response_type: null,
    suggestion_prompt: null,
    suggestion_example_data: null,
  };
  showAgentDrawer.value = true;
}

async function handleSavePrompt(_promptId: string, updates: PromptUpdate): Promise<void> {
  if (!isNewPrompt.value) {
    // Editing an existing prompt belongs to the prompt collection, which owns it.
    message.error('Editing existing prompts is not supported in this view');
    return;
  }
  if (isAddPromptOverCreditLimit.value) {
    message.error('Not enough credits to add this prompt.');
    return;
  }

  try {
    const promptCreateData: PromptCreate = {
      name: updates.name ?? '',
      prompt: updates.prompt || '',
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      response_type: updates.response_type || 'str',
      example_data: updates.example_data === undefined ? null : updates.example_data,
      evaluation_prompt: updates.evaluation_prompt,
      evaluation_response_type: updates.evaluation_response_type,
      suggestion_prompt: updates.suggestion_prompt,
      suggestion_example_data: updates.suggestion_example_data,
    };

    if (promptCollectionsStore.collections.length === 0) {
      await promptCollectionsStore.fetchCollections();
    }

    // A prompt has to belong to a collection, so an ad-hoc column lands in the
    // org's Default one.
    let defaultCollection = promptCollectionsStore.collections.find(c => c.name === 'Default');
    if (!defaultCollection) {
      const created = await promptCollectionsStore.createCollection('Default');
      if (!created) throw new Error('Failed to create a default collection');
      defaultCollection = created;
    }

    const createdPrompt = await promptCollectionsStore.addPrompt(
      defaultCollection.uid,
      promptCreateData
    );
    if (!createdPrompt) throw new Error('Failed to create prompt');

    const addResponse = await addPromptColumn({
      path: { review_uid: reviewId.value },
      body: createdPrompt,
    });
    if (addResponse.error) {
      throw new Error(extractErrorDetail(addResponse.error, 'Failed to add prompt to review'));
    }

    message.success('Prompt added to review successfully');
    await organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
    await fetchReviewData();
    showAgentDrawer.value = false;
    addPromptAgentCount.value = 1;
  } catch (error_) {
    message.error('Failed to add prompt to review');
    logger.error('Failed to add prompt to review', error_);
  }
}

function handleDraftAgentCountChange(count: number): void {
  if (isNewPrompt.value) addPromptAgentCount.value = count;
}

// ── Credits and cost preview ────────────────────────────────────────────────

const selectedAddFileInputCount = computed(
  () => addFileSelectedKeys.value.length + uploadSelectedCount.value
);

const addFileCostCurrentSignature = computed(() => {
  if (!reviewId.value || selectedAddFileInputCount.value <= 0) return '';
  const selectedKeySignature = [...addFileSelectedKeys.value].sort().join('|');
  return `${reviewId.value}:${selectedKeySignature}:${uploadSelectedCount.value}`;
});

const isAddFileCostReady = computed(
  () =>
    Boolean(addFileCost.value) &&
    !addFileCostLoading.value &&
    !addFileCostError.value &&
    addFileCostSignature.value === addFileCostCurrentSignature.value
);

const addFileRemainingCreditsAfterRun = computed(() => {
  if (!addFileCost.value) return null;
  return addFileCost.value.remaining_org_credits - addFileCost.value.total_cost;
});

const isAddFileOverCreditLimit = computed(
  () =>
    isAddFileCostReady.value &&
    addFileRemainingCreditsAfterRun.value !== null &&
    addFileRemainingCreditsAfterRun.value < 0
);

const addPromptCostDetails = computed(() => {
  const remainingCredits =
    addFileCost.value?.remaining_org_credits ??
    (organizationStore.activeOrganization
      ? organizationStore.activeOrganization.allowed_insights_credits -
        (organizationStore.organizationAllowanceUsage ?? 0)
      : null);
  const allowedCredits =
    addFileCost.value?.allowed_org_credits ??
    organizationStore.activeOrganization?.allowed_insights_credits ??
    null;
  const agentCount = Math.max(1, addPromptAgentCount.value);
  const fileCount = totalReviewRows.value || totalRows.value;
  const totalCost = fileCount * agentCount;
  const remainingAfter = remainingCredits === null ? null : remainingCredits - totalCost;

  return { allowedCredits, agentCount, fileCount, remainingAfter, totalCost };
});

const addPromptCreditDeficit = computed(() => {
  const remainingAfter = addPromptCostDetails.value.remainingAfter;
  if (remainingAfter === null || remainingAfter >= 0) return 0;
  return Math.abs(remainingAfter);
});

const isAddPromptOverCreditLimit = computed(
  () => isNewPrompt.value && addPromptCreditDeficit.value > 0
);

const addPromptCostText = computed(() => {
  const { allowedCredits, agentCount, fileCount, remainingAfter, totalCost } =
    addPromptCostDetails.value;

  const creditStatus = isAddPromptOverCreditLimit.value
    ? `Not enough credits. You need ${formatCredits(addPromptCreditDeficit.value)} more credit${addPromptCreditDeficit.value === 1 ? '' : 's'}. Please contact GAIA Support.`
    : `Remaining: ${formatCredits(remainingAfter)} of ${formatCredits(allowedCredits ?? 0)} credits`;

  return [
    `Cost: ${formatCredits(totalCost)} credits`,
    `${fileCount} file${fileCount === 1 ? '' : 's'} · ${agentCount} agent${agentCount === 1 ? '' : 's'}`,
    creditStatus,
  ].join('\n');
});

function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

async function fetchAddFileCost(
  reviewUid: string,
  numFiles: number,
  signature: string,
  requestSequence: number
): Promise<void> {
  try {
    const response = await getReviewCost({
      query: { review_uid: reviewUid, num_files: numFiles },
    });

    // A slower earlier request must not overwrite a newer selection's estimate.
    if (
      requestSequence !== addFileCostRequestSequence.value ||
      signature !== addFileCostCurrentSignature.value
    ) {
      return;
    }

    if (!response.data) throw new Error('Failed to calculate review cost');

    addFileCost.value = response.data;
    addFileCostSignature.value = signature;
    addFileCostError.value = null;
  } catch (costError) {
    if (requestSequence !== addFileCostRequestSequence.value) return;

    logger.error('Failed to calculate add files cost:', costError);
    addFileCost.value = null;
    addFileCostSignature.value = '';
    addFileCostError.value = extractErrorDetail(
      costError,
      'Failed to calculate cost for adding files.'
    );
  } finally {
    if (requestSequence === addFileCostRequestSequence.value) {
      addFileCostLoading.value = false;
    }
  }
}

const debouncedFetchAddFileCost = useDebounceFn(fetchAddFileCost, 400);

async function collectDirectoryFileUids(
  directoryUid: string,
  fileUids: Set<string>,
  visitedDirectoryUids: Set<string>
): Promise<void> {
  if (visitedDirectoryUids.has(directoryUid)) return;
  visitedDirectoryUids.add(directoryUid);

  const directoryItems = await vaultStore.getWholeDirectoryItems(directoryUid);
  await Promise.all(
    directoryItems.map(async (item: VaultItem) => {
      if (item.type === 'file') {
        fileUids.add(item.key.replace('file-', ''));
        return;
      }
      await collectDirectoryFileUids(item.key.replace('dir-', ''), fileUids, visitedDirectoryUids);
    })
  );
}

/** A selected directory bills for every file inside it, so it has to be walked. */
async function getSelectedAddFileBillableCount(
  selectedKeys: string[],
  selectedUploadCount: number
): Promise<number> {
  const fileUids = new Set(
    selectedKeys.filter(key => key.startsWith('file-')).map(key => key.replace('file-', ''))
  );
  const directoryUids = selectedKeys
    .filter(key => key.startsWith('dir-'))
    .map(key => key.replace('dir-', ''));
  const gaiaFileCount = new Set(
    selectedKeys.filter(key => key.startsWith('gaia-')).map(key => key.replace('gaia-', ''))
  ).size;
  const visitedDirectoryUids = new Set<string>();

  await Promise.all(
    directoryUids.map(directoryUid =>
      collectDirectoryFileUids(directoryUid, fileUids, visitedDirectoryUids)
    )
  );

  return fileUids.size + gaiaFileCount + selectedUploadCount;
}

async function queueAddFileCostRefresh(): Promise<void> {
  addFileCostRequestSequence.value++;
  const requestSequence = addFileCostRequestSequence.value;
  const signature = addFileCostCurrentSignature.value;
  const selectedKeys = [...addFileSelectedKeys.value];
  const selectedUploadCount = uploadSelectedCount.value;

  addFileCost.value = null;
  addFileCostError.value = null;
  addFileCostSignature.value = '';

  if (
    !showAddFileModal.value ||
    !reviewId.value ||
    selectedKeys.length + selectedUploadCount <= 0 ||
    !signature
  ) {
    addFileCostLoading.value = false;
    return;
  }

  addFileCostLoading.value = true;

  try {
    const numFiles = await getSelectedAddFileBillableCount(selectedKeys, selectedUploadCount);

    if (
      requestSequence !== addFileCostRequestSequence.value ||
      signature !== addFileCostCurrentSignature.value
    ) {
      return;
    }

    if (numFiles <= 0) {
      addFileCostLoading.value = false;
      addFileCostError.value = 'No files were found in the current selection.';
      return;
    }

    void debouncedFetchAddFileCost(reviewId.value, numFiles, signature, requestSequence);
  } catch (costError) {
    if (requestSequence !== addFileCostRequestSequence.value) return;

    addFileCost.value = null;
    addFileCostSignature.value = '';
    addFileCostLoading.value = false;
    addFileCostError.value = extractErrorDetail(
      costError,
      'Failed to calculate cost for the selected files.'
    );
  }
}

watch(
  [showAddFileModal, addFileSelectedKeys, uploadSelectedCount, reviewId],
  () => {
    void queueAddFileCostRefresh();
  },
  { deep: true }
);

// ── Add files ───────────────────────────────────────────────────────────────

async function handleAddFilesToReview(): Promise<void> {
  if (!reviewId.value) return;

  const hasUploadFiles = (addFileSelectorRef.value?.getSelectedFiles().length ?? 0) > 0;
  if (addFileSelectedKeys.value.length === 0 && !hasUploadFiles) {
    message.info('No files selected for addition.');
    return;
  }
  if (!isAddFileCostReady.value) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    message.error(addFileCostError.value || 'Please wait for the current cost estimate.');
    return;
  }
  if (isAddFileOverCreditLimit.value) {
    message.error('Not enough credits to add these files.');
    return;
  }

  processingAddFile.value = true;

  try {
    const uploadedFileUids = (await addFileSelectorRef.value?.uploadSelectedFiles()) ?? [];
    showAddFileModal.value = false;

    const fileUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('file-'))
      .map(key => key.replace('file-', ''));
    const dirUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('dir-'))
      .map(key => key.replace('dir-', ''));
    const gaiaUids = addFileSelectedKeys.value
      .filter(key => key.startsWith('gaia-'))
      .map(key => key.replace('gaia-', ''));

    const importedFileUids: string[] = [];
    if (gaiaUids.length > 0) {
      message.info(`Importing ${gaiaUids.length} GAIA file(s) to vault...`);
      for (const [index, gaiaFileId] of gaiaUids.entries()) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        const fileName = gaiaStore.getGaiaFileById(gaiaFileId)?.title || `File ${gaiaFileId}`;
        try {
          message.info(`Importing ${fileName} (${index + 1}/${gaiaUids.length})...`);
          const imported = await gaiaStore.importGaiaFileToVault(gaiaFileId);
          importedFileUids.push(imported.uid);
        } catch (error_) {
          logger.error(`Failed to import GAIA file ${gaiaFileId}:`, error_);
          message.error(
            `Failed to import ${fileName}: ${extractErrorDetail(error_, 'Failed to add files to review. Please try again.')}`
          );
          return;
        }
      }
      message.success(`Successfully imported ${importedFileUids.length} GAIA file(s) to vault`);
      await vaultStore.getVaultDirectory();
    }

    const combinedFileUids = [...new Set([...fileUids, ...importedFileUids, ...uploadedFileUids])];

    const payload: FileReviewCreateUser = {
      vault_file_uids: combinedFileUids,
      vault_directory_uids: dirUids,
      ocr_provider: 'unstructured' as const,
    };

    const createResponse = await createFileReviews({
      path: { review_uid: reviewId.value },
      body: payload,
    });
    if (createResponse.error) {
      throw new Error(
        extractErrorDetail(createResponse.error, 'Failed to add files to review. Please try again.')
      );
    }

    message.success(`${combinedFileUids.length + dirUids.length} file(s) queued for addition.`);
    await organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
    await fetchReviewData();
  } catch (error_) {
    logger.error('Error adding files to review:', error_);
    message.error(
      `Error adding files: ${extractErrorDetail(error_, 'Failed to add files to review. Please try again.')}`
    );
  } finally {
    processingAddFile.value = false;
  }
}

function handleAddFileModalClose(): void {
  addFileSelectedKeys.value = [];
  uploadSelectedCount.value = 0;
  addFileCost.value = null;
  addFileCostLoading.value = false;
  addFileCostError.value = null;
  addFileCostSignature.value = '';
  addFileCostRequestSequence.value++;
}

// ── CSV ─────────────────────────────────────────────────────────────────────

async function exportCsv(): Promise<void> {
  if (tableData.value.length === 0) {
    message.error('No data to export');
    return;
  }
  if (csvExportLoading.value) return;

  csvExportLoading.value = true;
  try {
    await downloadReviewCsv(
      reviewId.value,
      { ...csvExportOptions.value },
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      `${currentReview.value?.name || 'review'}_export.csv`
    );
    message.success('CSV export started');
    showCsvExportModal.value = false;
  } catch (error_) {
    logger.error('Error exporting CSV:', error_);
    message.error(`Export failed: ${extractErrorDetail(error_, 'Unknown error')}`);
  } finally {
    csvExportLoading.value = false;
  }
}

// ── Lifecycle ───────────────────────────────────────────────────────────────

async function ensureCreditContext(): Promise<void> {
  if (!organizationStore.activeOrganization) {
    await organizationStore.fetchActiveOrganization();
    return;
  }
  await organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
}

onMounted(async () => {
  try {
    await Promise.all([
      promptCollectionsStore.fetchCollections(),
      vaultStore.getVaultDirectory(),
      gaiaStore.fetchCurrentToken(),
      ensureCreditContext(),
    ]);
    await fetchReviewData();
  } catch (error_) {
    logger.error('Error initializing data:', error_);
    message.error('Failed to load initial data');
  }

  if (integrationsStore.activeIntegrations.gaia) {
    gaiaStore.fetchSectionGroups().catch((error_: unknown) => {
      logger.error('Error fetching GAIA section groups:', error_);
    });
  }
});

const { scheduleRefetch } = useReviewLive(reviewId.value, refreshTableSilently);
useReviewPolling(reviewId.value, () => {
  scheduleRefetch();
});
</script>

<template>
  <layout
    :content-max-width="gaiaContentWidth.table"
    :drawer-open="cellDrawerOpen"
    :drawer-width="480"
  >
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'reviews', query: { view: 'table' } }">
          {{ navLabels.reviews }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ currentReview?.name || 'Loading…' }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-page-header :title="currentReview?.name || 'Loading…'">
        <template #meta>
          <gaia-dropdown
            :options="stateOptions"
            @select="handleStateChange"
            @update:show="show => show && loadStates()"
          >
            <gaia-status-chip tone="neutral" size="small" :style="{ cursor: 'pointer' }">
              {{ currentStateName }}
            </gaia-status-chip>
          </gaia-dropdown>
        </template>
        <template #actions>
          <gaia-button size="small" @click="showCsvExportModal = true">Export CSV</gaia-button>
          <!-- Authoring a blank prompt brings a record into being, so it is a
               create; the files already exist in the vault, so they are added. -->
          <gaia-create-button
            v-if="canEdit"
            subject="prompt"
            variant="default"
            size="sm"
            :icon="false"
            @click="handleCreatePrompt"
          />
          <gaia-add-button
            v-if="canEdit"
            subject="files"
            variant="primary"
            :icon="false"
            :disabled="processingAddFile"
            @click="showAddFileModal = true"
          />
        </template>
      </gaia-page-header>

      <gaia-banner v-if="error" type="error">{{ error }}</gaia-banner>

      <gaia-summary-card v-if="hasSummary">
        <markdown-renderer :content="markdownSummary" />
      </gaia-summary-card>

      <n-flex align="center" :size="12" :wrap="false">
        <gaia-text size="meta" tone="subtle">Show</gaia-text>
        <gaia-select
          v-model:value="displayMode"
          :options="DISPLAY_MODE_OPTIONS"
          size="small"
          :style="{ width: '180px' }"
        />
        <!-- Right-bound on a wrapper: GaiaPopover binds its own props and lets
             attrs fall through to the floating content, so a style set on it
             never reaches this flex row. -->
        <n-flex
          align="center"
          :size="8"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-button v-if="hasActiveFilters" size="small" @click="resetFilters">
            Reset filters
          </gaia-button>
          <gaia-popover v-if="canEdit && columnMenuItems.length > 0" placement="bottom-end">
            <template #trigger>
              <gaia-button size="small">
                Columns
                <gaia-text size="meta" tone="faint" :style="{ fontFamily: monoFont }">
                  {{ columnMenuItems.length }}
                </gaia-text>
              </gaia-button>
            </template>
            <!-- GaiaPopover is `raw` — it paints nothing of its own, so the
                 content brings the surface. Same GaiaCard the filter panel's
                 menus use. -->
            <gaia-card content-style="padding: 10px 12px" :style="{ width: '268px' }">
              <gaia-removable-list
                intent="delete"
                :items="columnMenuItems"
                label="Columns"
                flush
                empty-text="This review has no prompt columns."
                @remove="uid => (pendingColumnDelete = uid)"
              />
            </gaia-card>
          </gaia-popover>
        </n-flex>
      </n-flex>

      <gaia-empty-state
        v-if="!loading && cachedColumns.length === 0"
        title="No extraction data yet"
      >
        Add files or prompts to this review, and its grid appears here.
      </gaia-empty-state>

      <template v-else>
        <!-- `single-line=false`: naive draws row dividers but suppresses the
             column ones while it holds, and a spreadsheet needs the grid both
             ways. -->
        <gaia-table
          :columns="cachedColumns"
          :data="tableData"
          :loading="tableLoading || loading"
          remote
          :pagination="false"
          :scroll-x="tableScrollX"
          :single-line="false"
          @update:sorter="handleSortChange"
          @update:filters="handleFiltersChange"
        />
        <gaia-pagination
          :page="currentPage"
          :page-size="pageSize"
          :total="totalRows"
          @update:page="page => (currentPage = page)"
          @update:page-size="
            size => {
              pageSize = size;
              currentPage = 1;
            }
          "
        />
      </template>
    </n-flex>

    <template #drawer>
      <gaia-panel :title="selectedField?.field ?? 'Cell'">
        <template #header-extra>
          <gaia-button size="tiny" quaternary @click="cellDrawerOpen = false">Close</gaia-button>
        </template>
        <gaia-extraction-detail
          v-if="selectedField"
          :field="selectedField"
          :can-edit="canEdit"
          :is-admin="isAdmin"
          @submit="submitOverwrite"
          @approve="handleApproval"
        />
      </gaia-panel>
    </template>
  </layout>

  <agent-edit-drawer
    v-model:show="showAgentDrawer"
    :prompt="currentAgentPrompt"
    :is-new="isNewPrompt"
    :save-notice="isNewPrompt ? addPromptCostText : undefined"
    :save-disabled="isNewPrompt && isAddPromptOverCreditLimit"
    @draft-agent-count-change="handleDraftAgentCountChange"
    @save="handleSavePrompt"
  />

  <gaia-modal v-model:show="showCsvExportModal" title="Export CSV" width="420px">
    <n-flex vertical :size="4" :wrap="false">
      <gaia-toggle-row v-model:checked="csvExportOptions.include_prompt" label="Include prompt" />
      <gaia-toggle-row
        v-model:checked="csvExportOptions.include_evaluation"
        label="Include evaluation"
      />
      <gaia-toggle-row v-model:checked="csvExportOptions.include_reasons" label="Include reasons" />
    </n-flex>
    <template #action>
      <gaia-button :disabled="csvExportLoading" @click="showCsvExportModal = false">
        Cancel
      </gaia-button>
      <gaia-button type="primary" :loading="csvExportLoading" @click="exportCsv"
        >Export</gaia-button
      >
    </template>
  </gaia-modal>

  <gaia-modal
    v-model:show="showAddFileModal"
    title="Add files to review"
    width="min(90vw, 1000px)"
    height="85vh"
    @update:show="value => !value && handleAddFileModalClose()"
  >
    <file-selector
      ref="addFileSelectorRef"
      v-model:selected-keys="addFileSelectedKeys"
      vault-layout="table"
      @upload-selection-change="uploadSelectedCount = $event"
    />
    <template #action>
      <n-flex vertical :size="10" :wrap="false" :style="{ width: '100%' }">
        <cost-preview-notice
          v-if="selectedAddFileInputCount > 0"
          :preview="addFileCost"
          :loading="addFileCostLoading"
          :error="addFileCostError"
          :ready="isAddFileCostReady"
        />
        <n-flex align="center" justify="end" :size="8" :wrap="false">
          <gaia-button @click="showAddFileModal = false">Cancel</gaia-button>
          <gaia-add-button
            subject="files"
            variant="primary"
            size="md"
            :icon="false"
            :disabled="
              processingAddFile ||
              (addFileSelectedKeys.length === 0 && uploadSelectedCount === 0) ||
              (selectedAddFileInputCount > 0 && !isAddFileCostReady) ||
              isAddFileOverCreditLimit
            "
            :loading="processingAddFile"
            @click="handleAddFilesToReview"
          />
        </n-flex>
      </n-flex>
    </template>
  </gaia-modal>

  <gaia-modal
    :show="pendingColumnDelete !== null"
    title="Delete column"
    @update:show="value => !value && (pendingColumnDelete = null)"
  >
    <gaia-text>
      Delete this prompt column? Its extracted values go with it, and this cannot be undone.
    </gaia-text>
    <template #action>
      <gaia-button @click="pendingColumnDelete = null">Cancel</gaia-button>
      <gaia-button type="error" @click="confirmColumnDelete">Delete</gaia-button>
    </template>
  </gaia-modal>
</template>
